'use client';

import React, { useEffect, useMemo, useRef } from 'react';

import { CloseButton, Skeleton, SkeletonText, Stack, SystemStyleObject } from '@chakra-ui/react';

import { mapAccommodationSlice, useAppDispatch, useAppSelector } from '@app/app-state';
import { AccommodationItem } from '@app/components/molecules/accommodation-item';
import { useMapAccommodationMapper } from '@app/hooks/useMapAccommodationMapper';
import { AccommodationItemVariantEnum, SelectedAccommodationType } from '@app/models';

export const MapPopoverContent = ({
    onClose,
    btnStyle,
    popoverData,
}: {
    onClose: () => void;
    btnStyle: SystemStyleObject;
    popoverData: SelectedAccommodationType;
}) => {
    const dispatch = useAppDispatch();
    const { selectAccommodationById } = mapAccommodationSlice.selectors;

    const accommodationSelector = useMemo(
        () => selectAccommodationById(popoverData.id),
        [popoverData.id, selectAccommodationById],
    );
    const accommodation = useAppSelector(accommodationSelector);

    const hasFetchedRef = useRef(false);

    const accommodationItemData = useMapAccommodationMapper({
        accommodationData: accommodation,
        popoverData: popoverData,
    });

    useEffect(() => {
        if (popoverData && !accommodation && !hasFetchedRef.current) {
            hasFetchedRef.current = true;
            dispatch(mapAccommodationSlice.actions.getAccommodationForMap(popoverData.id));
        }
    }, [popoverData, accommodation, dispatch]);

    return !accommodationItemData ? (
        <Stack gap="6" width="300px" padding={3} cursor={'default'}>
            <Skeleton height="180px" />
            <SkeletonText noOfLines={2} />
        </Stack>
    ) : (
        <>
            <CloseButton
                data-testid="popover-close-button"
                css={btnStyle}
                onClick={onClose}
                size="xs"
            />
            <AccommodationItem
                {...accommodationItemData}
                data-testid="map-accommodation-item"
                variant={AccommodationItemVariantEnum.Map}
            />
        </>
    );
};
